import { combineReducers } from 'redux'
import * as last from 'lodash/last'
import { Reducer } from 'interfaces'
import { ACTIVATE_ANCHOR, PUSH, SET_NAVIGATION_PARAMS } from 'actions/navigationActions'
import { PAGE_CHANGED } from 'actions/globalActions'
import { ActiveAnchorState, NavigationAppState } from 'interfaces/navigation'
import { isInternalPage } from 'helpers/pageHelpers'
import { URL_PAGE_MAP } from 'constants/pages'
import { REDEMPTION_ENTRY_URL, REWARDS_URL } from 'helpers/tangleSharedHelpers'

const activeAnchorReducer: Reducer<ActiveAnchorState> = (state = null, action) => {
  if (action.type === ACTIVATE_ANCHOR) {
    const { to, options } = action.payload
    if (to) {
      return {
        ...state,
        current: to,
        options,
      }
    } else {
      return null
    }
  }

  return state
}

const navigateToUrlReducer: Reducer<NavigationAppState['navigateToUrl']> = (state = null, action) => {
  switch (action.type) {
    case PAGE_CHANGED:
      return state === action.payload.next ? null : state

    case PUSH:
      return action.payload

    default:
      return state
  }
}

const paramsReducer: Reducer<Record<string, string>> = (prevState = {}, action) => {
  switch (action.type) {
    case SET_NAVIGATION_PARAMS:
      return action.payload

    default:
      return prevState
  }
}

const DEFAULT_PREVIOUS_URLS: string[] = []
export const previousUrlsReducer: Reducer<NavigationAppState['previousUrls']> = (state = DEFAULT_PREVIOUS_URLS, action) => {
  if (action.type === PAGE_CHANGED && action.payload.prev) {
    const { prev, next } = action.payload
    const lastVisitedUrl = last(state)

    if (lastVisitedUrl === next) {
      return dropRevisitedUrl(next, state)
    }

    if (lastVisitedUrl === prev) {
      return state
    }

    let nextState = state
    // FRO-2185 User can't go back from Rewards page after cancelling redemption
    const isFromRedemptionToRewards = prev.includes(REDEMPTION_ENTRY_URL) && next === REWARDS_URL

    if (isFromRedemptionToRewards) {
      nextState = nextState.filter((url) => !url.includes(REDEMPTION_ENTRY_URL))
      nextState = dropRevisitedUrl(next, nextState)
    }

    const previousPage = URL_PAGE_MAP[prev]
    const shouldIgnorePrevious = !isInternalPage(previousPage) || isFromRedemptionToRewards

    return shouldIgnorePrevious
      ? nextState
      : [...nextState, prev]
  }

  return state
}

function dropRevisitedUrl (next: string, state: NavigationAppState['previousUrls']) {
  const lastVisitedUrl = last(state)

  if (
    lastVisitedUrl !== next
    // don't drop the only page in the state
    || state.length <= 1
  ) {
    return state
  }

  return state.slice(0, state.length - 1)
}

const { hash, host, hostname, href, origin, pathname, port, protocol, search } = document.location
const INITIAL_LOCATION = { hash, host, hostname, href, origin, pathname, port, protocol, search } as typeof document.location
const initialLocation: Reducer<NavigationAppState['initialLocation']> = (prevState = INITIAL_LOCATION) => {
  return prevState
}

export default combineReducers<NavigationAppState>({
  params: paramsReducer,
  activeAnchor: activeAnchorReducer,
  navigateToUrl: navigateToUrlReducer,
  previousUrls: previousUrlsReducer,
  initialLocation,
})

